import { Box } from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { WishlistAvailabilitySearchConnectorProps } from "./container";
import { RouterProps, useLocation } from "react-router";
import { useSelector } from "react-redux";
import { useUpdateWishlistMutation, wishlistsApi } from "@capone/common";
import { CalendarPickerButton } from "../../../search/components/HotelSearchControl/components/CalendarPickerButton";
import { OccupancySelection } from "../../../search/components/HotelSearchControl/components/OccupancySelection";
import clsx from "clsx";
import { HotelSearchButton } from "../../../search/components/HotelSearchControl/components/SearchButton";
import dayjs from "dayjs";
import { FAVORITES_EDITED_LIST, FAVORITES_VIEWED_LIST } from "redmond";
import { trackEvent } from "../../../../api/v0/analytics/trackEvent";

export interface IWishlistAvailabilitySearch
  extends WishlistAvailabilitySearchConnectorProps,
    RouterProps {}

export const WishlistAvailabilitySearch = (
  props: IWishlistAvailabilitySearch
) => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const [wishlistId, setWishlistId] = useState<string | undefined>(undefined);

  const {
    fromDate,
    untilDate,
    adultsCount,
    childrenCount,
    petsCount,
    roomsCount,
    areRequestParametersChanged,
    lodgings,
  } = props;

  useEffect(() => {
    const id = searchParams.get("id");
    if (id) {
      setWishlistId(id);
    }
  }, []);

  const wishlistMetadata = useSelector(
    wishlistsApi.endpoints.getWishlistById.select({
      wishlistId: { value: wishlistId ?? "" },
    })
  )?.data?.wishlist?.wishlistData;

  useEffect(() => {
    if (lodgings.length > 0) {
      const unavailableProperties = lodgings.filter(
        (lodging) => !lodging.available
      );
      if (wishlistId && fromDate && untilDate) {
        const startOfToday = dayjs().startOf("day");
        const advance = dayjs(fromDate).diff(startOfToday, "day");
        const los = dayjs(untilDate).diff(dayjs(fromDate), "day");
        trackEvent({
          eventName: FAVORITES_VIEWED_LIST,
          properties: {
            favorites_id: wishlistId,
            advance,
            los,
            number_of_properties: lodgings.length,
            unavailable_properties: unavailableProperties.length,
          },
        });
      }
    }
  }, [lodgings]);

  const [updateWishlist] = useUpdateWishlistMutation();

  const handleSearch = () => {
    const datesChanged =
      dayjs(fromDate).format("YYYY-MM-DD") !== wishlistMetadata?.checkInDate ||
      dayjs(untilDate).format("YYYY-MM-DD") !== wishlistMetadata?.checkOutDate;

    const guestsChanged =
      adultsCount !== wishlistMetadata?.adults ||
      childrenCount.length !== wishlistMetadata?.children ||
      petsCount !== (wishlistMetadata?.petsIncluded ? 1 : 0);

    updateWishlist({
      wishlistId: { value: wishlistId ?? "" },
      wishlistData: {
        name: wishlistMetadata?.name || "List",
        checkInDate: dayjs(fromDate).format("YYYY-MM-DD"),
        checkOutDate: dayjs(untilDate).format("YYYY-MM-DD"),
        adults: adultsCount,
        children: childrenCount.length,
        petsIncluded: petsCount > 0,
        roomCount: roomsCount,
      },
    });

    if (datesChanged) {
      trackEvent({
        eventName: FAVORITES_EDITED_LIST,
        properties: {
          favorites_id: wishlistId,
          action: "edit_dates",
        },
      });
    }
    if (guestsChanged) {
      trackEvent({
        eventName: FAVORITES_EDITED_LIST,
        properties: {
          favorites_id: wishlistId,
          action: "edit_guests",
        },
      });
    }
  };

  return (
    <Box className="hotel-availability-search-container">
      <Box className={clsx("hotel-availability-search-control")}>
        <Box className="search-components-section">
          <Box className={clsx("hotel-search-control-root", "multiroom")}>
            <Box className={"hotel-search-control-row"}>
              <Box className="hotel-search-inputs">
                <Box className={"hotel-search-input-row"}>
                  <Box className={"date-input"}>
                    <CalendarPickerButton
                      saveDatesOnClose={false}
                      hasMissingSearchInfoError={false}
                      hideSeparator={false}
                      showCalenderPricingColors={false}
                      showCalendarPricing={false}
                    />
                  </Box>
                  <div className={"occupancy-selection"}>
                    <OccupancySelection
                      showTotalTravelers={true}
                      showPets={true}
                    />
                  </div>
                </Box>
              </Box>
              {areRequestParametersChanged && (
                <HotelSearchButton
                  className={clsx("hotel-search-control-button", "b2b")}
                  message={"Update"}
                  onClick={handleSearch}
                  enabled={true}
                />
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
